/**
 * Cliente: callable `createOnboardingInvite` (gera/reutiliza token e URL com ?invite=).
 * Resolve/submit via convite são HTTP no site (Cloud Functions), não aqui.
 *
 * Fase 4 — preferir `createOnboardingInviteForOnboarding` (stub «cliente preenche»).
 * `createOnboardingInviteForLead` = fluxo legado (onboarding nasce no submit do site).
 */

import { tsToMs } from './leadsRepo.js';

function logCreateOnboardingInvitePayload(payload) {
  try {
    console.info('[createOnboardingInvite] callable payload (JSON)', JSON.stringify(payload));
  } catch (_) {
    console.info('[createOnboardingInvite] callable payload', payload);
  }
}

/**
 * LEGADO: convite ligado só ao lead — o onboarding é criado quando o cliente submete no site.
 * Preferir {@link createOnboardingInviteForOnboarding} quando possível.
 *
 * @param {string} leadId
 * @returns {Promise<{ inviteUrl: string, token: string, expiresAt: number, reused: boolean }>}
 */
export async function createOnboardingInviteForLead(leadId) {
  if (typeof window === 'undefined' || typeof window._fbHttpsCallable !== 'function') {
    throw new Error('Firebase indisponível');
  }
  const id = String(leadId ?? '').trim();
  if (!id) {
    console.error('[createOnboardingInviteForLead] leadId vazio', { leadId });
    throw new Error('ID do lead em falta — não é possível gerar o convite.');
  }
  const payload = { leadId: id };
  logCreateOnboardingInvitePayload(payload);
  const fn = window._fbHttpsCallable('createOnboardingInvite');
  try {
    const res = await fn(payload);
    const d = res?.data;
    if (!d || !d.inviteUrl) throw new Error('Resposta inválida do servidor');
    return d;
  } catch (err) {
    const code = err && typeof err.code === 'string' ? err.code : '';
    const msg = err && typeof err.message === 'string' ? err.message : String(err);
    console.warn('[createOnboardingInviteForLead] falha', { code, message: msg, payload });
    throw err;
  }
}

/**
 * CANÓNICO (Fase 4): convite para onboarding já existente (`dashboardSelfFillPending`).
 *
 * @param {string} onboardingId
 * @returns {Promise<{ inviteUrl: string, token: string, expiresAt: number, reused: boolean }>}
 */
export async function createOnboardingInviteForOnboarding(onboardingId) {
  if (typeof window === 'undefined' || typeof window._fbHttpsCallable !== 'function') {
    throw new Error('Firebase indisponível');
  }
  const id = String(onboardingId ?? '').trim();
  if (!id) {
    console.error('[createOnboardingInviteForOnboarding] onboardingId vazio', { onboardingId });
    throw new Error('ID do onboarding em falta — não é possível gerar o convite.');
  }
  const payload = { onboardingId: id };
  logCreateOnboardingInvitePayload(payload);
  const fn = window._fbHttpsCallable('createOnboardingInvite');
  try {
    const res = await fn(payload);
    const d = res?.data;
    if (!d || !d.inviteUrl) throw new Error('Resposta inválida do servidor');
    return d;
  } catch (err) {
    const code = err && typeof err.code === 'string' ? err.code : '';
    const msg = err && typeof err.message === 'string' ? err.message : String(err);
    console.warn('[createOnboardingInviteForOnboarding] falha', { code, message: msg, payload });
    throw err;
  }
}

/**
 * Snapshot dos convites do lead (ordenado: `createdAt` desc no cliente).
 * @param {string} leadId
 * @param {(rows: object[]) => void} onData
 * @param {(err: unknown) => void} [onError]
 */
export function subscribeOnboardingInvitesForLead(leadId, onData, onError) {
  const id = String(leadId || '').trim();
  const db = typeof window !== 'undefined' ? window._fbDb : null;
  if (!id || !db || !window._fbCollection || !window._fbQuery || !window._fbWhere || !window._fbLimit) {
    onData([]);
    return () => {};
  }
  const col = window._fbCollection(db, 'onboardingInvites');
  const q = window._fbQuery(col, window._fbWhere('leadId', '==', id), window._fbLimit(40));
  return window._fbOnSnapshot(
    q,
    snap => {
      const rows = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      rows.sort((a, b) => (Number(b.createdAt) || 0) - (Number(a.createdAt) || 0));
      onData(rows);
    },
    err => {
      if (typeof onError === 'function') onError(err);
      onData([]);
    },
  );
}

/**
 * @param {string} onboardingId
 * @param {(rows: object[]) => void} onData
 * @param {(err: unknown) => void} [onError]
 */
export function subscribeOnboardingInvitesForOnboarding(onboardingId, onData, onError) {
  const id = String(onboardingId || '').trim();
  const db = typeof window !== 'undefined' ? window._fbDb : null;
  if (!id || !db || !window._fbCollection || !window._fbQuery || !window._fbWhere || !window._fbLimit) {
    onData([]);
    return () => {};
  }
  const col = window._fbCollection(db, 'onboardingInvites');
  const q = window._fbQuery(col, window._fbWhere('onboardingId', '==', id), window._fbLimit(40));
  return window._fbOnSnapshot(
    q,
    snap => {
      const rows = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      rows.sort((a, b) => (Number(b.createdAt) || 0) - (Number(a.createdAt) || 0));
      onData(rows);
    },
    err => {
      if (typeof onError === 'function') onError(err);
      onData([]);
    },
  );
}

/**
 * Estados do funil de convite no painel do lead (hierarquia pedida).
 * @param {Record<string, unknown>|null} lead
 * @param {object[]} inviteRows — docs normalizados (incl. id token)
 */
export function computeOnboardingInvitePipelineState(lead, inviteRows) {
  const rows = Array.isArray(inviteRows) ? inviteRows : [];
  const now = Date.now();
  const onboardingId = String(lead?.onboardingId || '').trim();
  if (onboardingId) {
    return { step: 'submitted', label: 'Onboarding enviado', hint: `ID: ${onboardingId}` };
  }
  const anyUsed = rows.some(r => r.status === 'used' || r.usedAt != null);
  if (anyUsed) {
    return { step: 'submitted', label: 'Onboarding enviado', hint: 'Formulário enviado via convite' };
  }
  const active = rows.filter(
    r =>
      r.status === 'active' &&
      r.usedAt == null &&
      (typeof r.expiresAt !== 'number' || r.expiresAt > now),
  );
  if (!active.length) {
    return { step: 'none', label: 'Não enviado', hint: '' };
  }
  const inv = active[0];
  const openedMs = tsToMs(inv.openedAt);
  if (openedMs == null) {
    return { step: 'link', label: 'Link gerado', hint: 'Copie e envie ao lead' };
  }
  return { step: 'opened', label: 'Formulário aberto', hint: 'Lead abriu o link; aguardando envio' };
}

/**
 * Painel do onboarding criado no dashboard para o cliente preencher (convite por `onboardingId`).
 * @param {Record<string, unknown>|null} ob — doc normalizado (`normalizeOnboardingDoc`)
 * @param {object[]} inviteRows
 */
export function computeDashboardOnboardingInvitePipelineState(ob, inviteRows) {
  if (!ob?.dashboardSelfFillPending) return null;
  const rows = Array.isArray(inviteRows) ? inviteRows : [];
  const now = Date.now();
  const active = rows.filter(
    r =>
      r.status === 'active' &&
      r.usedAt == null &&
      (typeof r.expiresAt !== 'number' || r.expiresAt > now),
  );
  if (!active.length) {
    return { step: 'pending', label: 'A gerar convite…', hint: 'Guarde o registo e aguarde o link' };
  }
  const inv = active[0];
  const openedMs = tsToMs(inv.openedAt);
  if (openedMs == null) {
    return { step: 'sent', label: 'Convite enviado', hint: 'Copie o link e envie ao cliente' };
  }
  return { step: 'opened', label: 'Aguardando preenchimento', hint: 'Cliente abriu o formulário no site' };
}
